import { useState } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  TextFormatType,
  ElementFormatType
} from "lexical"
import { $setBlocksType } from "@lexical/selection"
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text"
import { $createParagraphNode } from "lexical"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Toggle } from "@/components/ui/toggle"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Separator } from "@/components/ui/separator"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Undo,
  Redo,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  Link,
  Eraser,
  Baseline,
  PaintBucket,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  IndentDecrease,
  IndentIncrease,
  Plus,
  Minus,
  Type,
  Text
} from "lucide-react"

export function Toolbar() {
  const [editor] = useLexicalComposerContext()
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [fontSize, setFontSize] = useState(16)
  const [blockType, setBlockType] = useState("paragraph")
  const [fontFamily, setFontFamily] = useState("Arial")

  // Register listeners for editor state changes
  editor.registerCommand(
    CAN_UNDO_COMMAND,
    (payload) => {
      setCanUndo(payload)
      return false
    },
    1
  )

  editor.registerCommand(
    CAN_REDO_COMMAND,
    (payload) => {
      setCanRedo(payload)
      return false
    },
    1
  )

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatElement = (format: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format)
  }

  const changeFontSize = (delta: number) => {
    const newSize = Math.max(1, Math.min(72, fontSize + delta))
    setFontSize(newSize)
  }

  const changeBlockType = (type: string) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        if (type === "paragraph") {
          $setBlocksType(selection, () => $createParagraphNode())
        } else if (type === "h1") {
          $setBlocksType(selection, () => $createHeadingNode("h1"))
        } else if (type === "h2") {
          $setBlocksType(selection, () => $createHeadingNode("h2"))
        } else if (type === "h3") {
          $setBlocksType(selection, () => $createHeadingNode("h3"))
        } else if (type === "quote") {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      }
    })
    setBlockType(type)
  }

  return (
    <div className="sticky top-0 z-10 flex gap-2 overflow-auto border-b p-1 bg-background">
      {/* Undo/Redo */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={!canUndo}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          title="Undo (⌘Z)"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={!canRedo}
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          title="Redo (⇧⌘Z)"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="h-8" />

      {/* Block Type */}
      <Select value={blockType} onValueChange={changeBlockType}>
        <SelectTrigger className="h-8 w-min gap-1">
          <Text className="h-4 w-4" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="paragraph">Paragraph</SelectItem>
          <SelectItem value="h1">Heading 1</SelectItem>
          <SelectItem value="h2">Heading 2</SelectItem>
          <SelectItem value="h3">Heading 3</SelectItem>
          <SelectItem value="quote">Quote</SelectItem>
        </SelectContent>
      </Select>

      {/* Font Family */}
      <Select value={fontFamily} onValueChange={setFontFamily}>
        <SelectTrigger className="h-8 w-min gap-1">
          <Type className="h-4 w-4" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Arial">Arial</SelectItem>
          <SelectItem value="Georgia">Georgia</SelectItem>
          <SelectItem value="Times New Roman">Times New Roman</SelectItem>
          <SelectItem value="Helvetica">Helvetica</SelectItem>
          <SelectItem value="Courier New">Courier New</SelectItem>
        </SelectContent>
      </Select>

      {/* Font Size */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => changeFontSize(-1)}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <Input
          type="number"
          min="1"
          max="72"
          value={fontSize}
          onChange={(e) => setFontSize(Number(e.target.value))}
          className="h-8 w-12 text-center"
        />
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => changeFontSize(1)}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      <Separator orientation="vertical" className="h-8" />

      {/* Text Formatting */}
      <Toggle
        pressed={isBold}
        onPressedChange={(pressed) => {
          setIsBold(pressed)
          formatText("bold")
        }}
        className="h-8 px-1.5 min-w-8"
        aria-label="Toggle bold"
      >
        <Bold className="h-4 w-4" />
      </Toggle>

      <Toggle
        pressed={isItalic}
        onPressedChange={(pressed) => {
          setIsItalic(pressed)
          formatText("italic")
        }}
        className="h-8 px-1.5 min-w-8"
        aria-label="Toggle italic"
      >
        <Italic className="h-4 w-4" />
      </Toggle>

      <Toggle
        pressed={isUnderline}
        onPressedChange={(pressed) => {
          setIsUnderline(pressed)
          formatText("underline")
        }}
        className="h-8 px-1.5 min-w-8"
        aria-label="Toggle underline"
      >
        <Underline className="h-4 w-4" />
      </Toggle>

      <Toggle
        pressed={isStrikethrough}
        onPressedChange={(pressed) => {
          setIsStrikethrough(pressed)
          formatText("strikethrough")
        }}
        className="h-8 px-1.5 min-w-8"
        aria-label="Toggle strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-8" />

      {/* Subscript/Superscript */}
      <ToggleGroup type="single" variant="outline" size="sm">
        <ToggleGroupItem value="subscript" className="h-8 px-1.5" aria-label="Toggle subscript">
          <Subscript className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="superscript" className="h-8 px-1.5" aria-label="Toggle superscript">
          <Superscript className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Link */}
      <Toggle className="h-8 w-8 p-0" aria-label="Toggle link">
        <Link className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-8" />

      {/* Clear Formatting */}
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label="Clear formatting"
      >
        <Eraser className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-8" />

      {/* Text Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            title="Text color"
          >
            <Baseline className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="grid grid-cols-8 gap-1">
            {/* Color palette would go here */}
          </div>
        </PopoverContent>
      </Popover>

      {/* Background Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            title="Background color"
          >
            <PaintBucket className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="grid grid-cols-8 gap-1">
            {/* Color palette would go here */}
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-8" />

      {/* Text Alignment */}
      <ToggleGroup type="single" variant="outline" size="sm" defaultValue="left">
        <ToggleGroupItem 
          value="left" 
          className="h-8 px-1.5"
          onClick={() => formatElement("left")}
          aria-label="Left Align"
        >
          <AlignLeft className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem 
          value="center" 
          className="h-8 px-1.5"
          onClick={() => formatElement("center")}
          aria-label="Center Align"
        >
          <AlignCenter className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem 
          value="right" 
          className="h-8 px-1.5"
          onClick={() => formatElement("right")}
          aria-label="Right Align"
        >
          <AlignRight className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem 
          value="justify" 
          className="h-8 px-1.5"
          onClick={() => formatElement("justify")}
          aria-label="Justify Align"
        >
          <AlignJustify className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      <Separator orientation="vertical" className="h-7" />

      {/* Indent */}
      <ToggleGroup type="single" variant="outline" size="sm">
        <ToggleGroupItem value="outdent" className="h-8 px-1.5" aria-label="Outdent">
          <IndentDecrease className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="indent" className="h-8 px-1.5" aria-label="Indent">
          <IndentIncrease className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      <Separator orientation="vertical" className="h-8" />

      {/* Insert Menu */}
      <Select>
        <SelectTrigger className="h-8 w-min gap-1">
          <Plus className="h-4 w-4" />
          <span>Insert</span>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="image">Image</SelectItem>
          <SelectItem value="table">Table</SelectItem>
          <SelectItem value="link">Link</SelectItem>
          <SelectItem value="hr">Horizontal Rule</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
